// What the Orders list draws (KOVA-797) — a port of the mobile app's `features/orders/model/build-orders-screen-model.ts`
// plus the "In progress" header rule of its `OrdersScreen.tsx`, and the web-only paging of "All orders" (spec §2.1).
// The sections are the server's `inProgress` / `past` as sent: never re-bucketed by status, never re-sorted.
import type { BannerAction, OrderSummary, OrdersBanner, OrdersOverview } from '@/api/medOrders'
import { formatCalendarWeekday } from '@/orders/format'
import {
	renewalActionItems,
	renewalBannerModel,
	type RenewalBannerModel,
	type RenewalTier,
} from '@/orders/renewalAction'
import { listBadge, type StatusBadge } from '@/orders/status'

export interface OrderListItem extends OrderSummary {
	/** `null` for a status this app doesn't know — the card still shows and opens. */
	badge: StatusBadge | null
}

export interface OrdersListModel {
	inProgress: OrderListItem[]
	past: OrderListItem[]
	/** Totals of the whole sections, not of the page shown; the renewal card is not counted. */
	inProgressCount: number
	pastCount: number
	/** Both sections empty — the "No orders yet" card. */
	isEmpty: boolean
	showInProgressHeader: boolean
	renewal: RenewalBannerModel | null
	/** "Wed 21 Oct", or `null` ("Not scheduled") when there is no estimate or it isn't a real day. */
	nextShipment: string | null
	/**
	 * The warning glyph next to the next-shipment date, in the renewal card's colour: the card's tier while it is at
	 * Warning or Error (docs/architecture/orders.md, Design deviations), `null` (no glyph) at Info or without a card.
	 */
	nextShipmentWarning: Exclude<RenewalTier, 'info'> | null
}

/** The "All orders" page size (spec Q6: nothing pins it; Figma's pager suggests 10). */
export const PAGE_SIZE = 10

export interface OrdersPage<T> {
	items: T[]
	/** 1-based, clamped into `1…pageCount`. */
	page: number
	/** At least 1, also for no items. */
	pageCount: number
}

// An in-progress order's bound renewal action: `banner.orderId === order.id`. The first in precedence order wins,
// so an order with both a payment and an intake action reads "Payment needed".
function actionByOrder(banners: readonly OrdersBanner[]): Map<string, BannerAction> {
	const actions = new Map<string, BannerAction>()
	for (const item of renewalActionItems(banners)) {
		if (item.orderId !== null && !actions.has(item.orderId)) actions.set(item.orderId, item.action)
	}
	return actions
}

/**
 * `banners`: the renewal feed, or `null` while it isn't loaded or its read failed — the list then draws without
 * a renewal card and without action badges (a feed failure never blocks the list, spec B10).
 */
export function buildOrdersListModel(
	overview: OrdersOverview,
	banners: readonly OrdersBanner[] | null,
): OrdersListModel {
	const feed = banners ?? []
	const actions = actionByOrder(feed)
	const renewal = renewalBannerModel(feed)
	const inProgressCount = overview.inProgress.length
	const isEmpty = inProgressCount === 0 && overview.past.length === 0
	return {
		inProgress: overview.inProgress.map((order) => ({
			...order,
			badge: listBadge(order, { inProgress: true, action: actions.get(order.id) }),
		})),
		past: overview.past.map((order) => ({ ...order, badge: listBadge(order, { inProgress: false }) })),
		inProgressCount,
		pastCount: overview.past.length,
		isEmpty,
		// With no orders at all the "No orders yet" card stands in for the header; with past orders only, a
		// renewal card still sits under "In progress" (count 0) as on mobile (spec L5, Q8).
		showInProgressHeader: inProgressCount > 0 || (renewal !== null && !isEmpty),
		renewal,
		nextShipment: overview.nextShipmentDate === null ? null : formatCalendarWeekday(overview.nextShipmentDate),
		nextShipmentWarning: renewal !== null && renewal.tier !== 'info' ? renewal.tier : null,
	}
}

/**
 * One page of `items` in their given order. A page out of range — after a refresh shrank the list, or a hand-typed
 * `?page=` — lands on the nearest real one: below 1 or not a number → 1, past the end → the last page. A fraction
 * rounds down.
 */
export function paginate<T>(items: readonly T[], page: number, pageSize: number = PAGE_SIZE): OrdersPage<T> {
	const pageCount = Math.max(1, Math.ceil(items.length / pageSize))
	const wanted = Number.isNaN(page) ? 1 : Math.floor(page)
	const current = Math.min(Math.max(wanted, 1), pageCount)
	const start = (current - 1) * pageSize
	return { items: items.slice(start, start + pageSize), page: current, pageCount }
}
